class PriceRange extends HTMLElement {
  constructor() {
    super()

    this.fromSlider = this.querySelectorAll("input[type='range']")[0]
    this.toSlider = this.querySelectorAll("input[type='range']")[1]
    this.fromInput = this.querySelectorAll("input[type='number']")[0]
    this.toInput = this.querySelectorAll("input[type='number']")[1]
    this.resetButton = this.querySelector("input[type='button'].range--reset")
    this.mobileLabelFrom = this.querySelector(".range--slider-label-from-value")
    this.mobileLabelTo = this.querySelector(".range--slider-label-to-value")

    this.rangeMin = this.toInput.min
    this.rangeMax = this.toInput.max
    this.rangeDistance = this.rangeMax - this.rangeMin

    this.sliderColor = this.dataset.sliderColor || "#b1b1b1"
    this.rangeColor = this.dataset.rangeColor || "#989898"

    this.setSlider(this.fromSlider.value, this.toSlider.value)
  }

  addListeners() {
    this.fromSlider.addEventListener("input", this.updateFromSlider.bind(this))
    this.toSlider.addEventListener("input", this.updateToSlider.bind(this))
    this.fromInput.addEventListener("input", this.updateFromInput.bind(this))
    this.fromInput.addEventListener("keypress", this.validateNumberInput)
    this.toInput.addEventListener("input", this.updateToInput.bind(this))
    this.toInput.addEventListener("keypress", this.validateNumberInput)
    this.resetButton.addEventListener("click", this.handleResetButtonClick.bind(this))
  }

  validateNumberInput(e) {
    if (e.charCode === 8 || (e.charCode >= 48 && e.charCode <= 57)) return
    e.preventDefault()
  }

  connectedCallback() {
    this.addListeners()
  }

  updateFromInput(e) {
    const [from, to] = this.getParsed(this.fromInput, this.toInput)
    const fromValue = from !== null ? from : Number(this.fromInput.min)
    const toValue = to || Number(this.toInput.max)

    if (fromValue < this.fromInput.min) {
      this.fromInput.value = Number(this.fromInput.min)
    } else if (fromValue > toValue) {
      this.fromInput.value = toValue
    }

    if (!this.fromInput.value) {
      this.setSlider(fromValue, false)
      this.mobileLabelFrom.innerHTML = fromValue
    } else if (this.fromInput.value <= toValue) {
      this.setSlider(this.fromInput.value, false)
      this.mobileLabelFrom.innerHTML = this.fromInput.value
      this.showResetButton()
    } else {
      e.stopPropagation()
    }
  }

  updateToInput(e) {
    const [from, to] = this.getParsed(this.fromInput, this.toInput)
    const toValue = to !== null ? to : Number(this.toInput.max)
    const fromValue = from || Number(this.fromInput.min)

    if (toValue > this.toInput.max) {
      this.toInput.value = Number(this.toInput.max)
    }

    if (!this.toInput.value) {
      this.setSlider(false, toValue)
      this.mobileLabelTo.innerHTML = toValue
    } else if (fromValue <= this.toInput.value) {
      this.setSlider(false, this.toInput.value)
      this.mobileLabelTo.innerHTML = this.toInput.value
      this.showResetButton()
    } else {
      e.stopPropagation()
    }
  }

  updateFromSlider() {
    const [from, to] = this.getParsed(this.fromSlider, this.toSlider)

    if (from > to) {
      this.fromSlider.value = to
      this.fromInput.value = to
    } else {
      this.fromInput.value = from
    }

    this.fromInput.dispatchEvent(new Event("input", { bubbles: true }))
  }

  updateToSlider() {
    const [from, to] = this.getParsed(this.fromSlider, this.toSlider)

    if (from <= to) {
      this.toInput.value = to
    } else {
      this.toInput.value = from
      this.toSlider.value = from
    }

    this.toInput.dispatchEvent(new Event("input", { bubbles: true }))
  }

  getParsed(currentFrom, currentTo) {
    const from = currentFrom.value ? parseInt(currentFrom.value, 10) : null
    const to = currentTo.value ? parseInt(currentTo.value, 10) : null
    return [from, to]
  }

  setSlider(from, to) {
    if (from !== false) {
      this.fromSlider.value = from
    }

    if (to !== false) {
      this.toSlider.value = to
    }
    const fromPosition = this.fromSlider.value - this.rangeMin
    const toPosition = this.toSlider.value - this.rangeMin

    const fromOffset = (fromPosition / this.rangeDistance) * 100
    const toOffset = (toPosition / this.rangeDistance) * 100

    // slider background color become off-center as you get toward the edges of the slider
    const thumbWidth = 16
    const halfThumbWidth = thumbWidth / 2
    const fromPixelPart = halfThumbWidth - fromOffset * 0.15
    const toPixelPart = halfThumbWidth - toOffset * 0.15

    this.fillSlider(fromOffset, toOffset, fromPixelPart, toPixelPart)
  }

  fillSlider(fromOffset, toOffset, fromPixelPart, toPixelPart) {
    this.toSlider.style.background = `linear-gradient(
      to right,
      ${this.sliderColor} 0%,
      ${this.sliderColor} calc(${fromOffset}% + ${fromPixelPart}px),
      ${this.rangeColor} calc(${fromOffset}% + ${fromPixelPart}px),
      ${this.rangeColor} calc(${toOffset}% + ${toPixelPart}px),
      ${this.sliderColor} calc(${toOffset}% + ${toPixelPart}px),
      ${this.sliderColor} 100%)`
  }

  handleResetButtonClick(e) {
    e.preventDefault()
    e.stopPropagation()

    this.fromInput.value = null
    this.toInput.value = null

    this.fromInput.dispatchEvent(new Event("input", { bubbles: true }))
    this.toInput.dispatchEvent(new Event("input", { bubbles: true }))
    this.hideResetButton()
  }

  removeListeners() {
    this.fromSlider.removeEventListener("input", this.updateFromSlider.bind(this))
    this.toSlider.removeEventListener("input", this.updateToSlider.bind(this))
    this.fromInput.removeEventListener("input", this.updateFromInput.bind(this))
    this.toInput.removeEventListener("input", this.updateToInput.bind(this))
    this.resetButton.removeEventListener("click", this.handleResetButtonClick.bind(this))
  }

  hideResetButton() {
    this.resetButton.classList.add("hidden")
  }

  showResetButton() {
    this.resetButton.classList.remove("hidden")
  }

  disconnectedCallback() {
    this.removeListeners()
  }
}

if (!customElements.get("price-range")) {
  customElements.define("price-range", PriceRange)
}
